// TIP-609 — the translation layer between the allowance endpoint and the page.
//
// Every component below useCounterpartyBriefs() consumes the shape that
// deriveAllowance() produces, and that shape is not the wire shape. Rather than
// touch eight components, this module maps one onto the other. The mapping is
// the only place in the frontend that knows the wire field names.
//
// Two rules are deliberately NOT re-derived here:
//
//   can_order — the backend decides whether ordering is possible. The fixture
//   path derives it (plan exhausted AND no bought balance); the live path obeys
//   the flag. If the two ever disagree the backend is right, because it also
//   knows about suspensions and other block_reasons the frontend cannot see.
//
//   the reset dates — sent pre-formatted as monthly_label / weekly_label. The
//   ISO timestamps ride along but nothing formats them: open questions 7 and 8
//   are unanswered, so a second calculation here would disagree with the
//   backend on screen.
import { BRIEF_STATUSES, ENTITLEMENTS } from "./briefsFixtures";

// A claimed business has a TREVEX string PK — TRXIN0000010, TRXAE0000042. An
// ID that does not carry the prefix is a directory listing nobody has claimed,
// and the allowance endpoint answers 404 for it. Checking the prefix first
// saves a request we know the answer to and gets the claim gate on screen
// without a round trip.
export const CLAIMED_ID_PREFIX = "TRX";

export const isClaimedCompanyId = (companyID) =>
  typeof companyID === "string" &&
  companyID.trim().toUpperCase().startsWith(CLAIMED_ID_PREFIX);

// claim_status is the fork above the product: `claimed` renders the allowance
// card and the briefs table, anything else renders the claim gate instead.
export const GATE_VARIANTS = { unclaimed: "unclaimed", pending: "pending" };

export const gateVariantFor = (claimStatus) =>
  GATE_VARIANTS[claimStatus] ?? GATE_VARIANTS.unclaimed;

// `bought` arrives as an object, `{ remaining: n }`, but the counter renders a
// number. Missing bucket means zero, never "unknown" — the counter is simply
// not drawn.
const boughtCount = (bought) => {
  const n = Number(bought?.remaining);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const counter = (bucket) =>
  bucket == null ? null : { remaining: Number(bucket.remaining) || 0, cap: Number(bucket.cap) || 0 };

/**
 * The wire payload for a claimed business → the shape deriveAllowance()
 * returns. Field-for-field the same contract, so no component changes.
 */
export const mapAllowance = (data) => {
  const isMember = data?.tier === "member";
  const monthly = counter(data?.allowance?.monthly) ?? { remaining: 0, cap: 0 };
  // Null, not zero: a Member has no weekly limit and the counter must be
  // absent from the DOM. `counter()` preserves the null.
  const week = counter(data?.allowance?.weekly);
  const bought = boughtCount(data?.allowance?.bought);
  const pricing = data?.pricing ?? {};

  const hasBought = bought > 0;
  const weekAtCap = week != null && week.remaining === 0;
  const monthAtCap = monthly.remaining === 0;

  // The backend's verdict, with the fixture rule as the fallback for a payload
  // that predates the flag.
  const blocked =
    typeof data?.can_order === "boolean" ? !data.can_order : (weekAtCap || monthAtCap) && !hasBought;

  let banner = null;
  if (blocked && monthAtCap) {
    banner = { tone: "coral", showMembershipPitch: !isMember };
  } else if (blocked && weekAtCap) {
    banner = { tone: "amber", showMembershipPitch: !isMember };
  }

  return {
    // The card reads month.allowance, not month.cap — the wire name is `cap`.
    month: { remaining: monthly.remaining, allowance: monthly.cap },
    week,
    bought,
    isMember,
    // The prices the page prints. Sent formatted with the currency; the page
    // holds no price of its own, so a missing field falls back to config
    // rather than to a literal.
    tier: {
      unit: pricing.unit ?? ENTITLEMENTS[isMember ? "member" : "claimed"].unit,
      unitLabel: pricing.unit_label ?? ENTITLEMENTS[isMember ? "member" : "claimed"].unitLabel,
      monthlyAllowance: monthly.cap,
      weeklyCap: week?.cap ?? null,
    },
    // Always the Member price: what membership would cost the reader, not what
    // they pay today. The coral banner sells on this number.
    memberUnit: pricing.member_unit ?? ENTITLEMENTS.member.unit,
    // TIP-614 — the RAW block, kept as the endpoint sent it. The checkout
    // computes its line items from these fils and the VAT rate rather than
    // from the frontend's mirrored constants, so a price change in settings
    // reaches the screen instead of being silently overridden. Passed through
    // untransformed on purpose: the pricing module reads the wire names.
    pricing,
    memberDiscountApplied: pricing.member_discount_applied === true,

    hasBought,
    blocked,
    banner,
    // The month note explains why a real number is unreachable today.
    monthHeldByWeek: blocked && weekAtCap && !monthAtCap,
    weekAtCap,
    // A Member already holds every benefit the pitch sells.
    showMembershipCta: !isMember,

    resetDate: data?.reset?.monthly_label ?? null,
    weekResetDate: data?.reset?.weekly_label ?? null,

    // Carried through for TIP-611: the order form's quantity stepper and the
    // confirmation's "drawn from" line. Nothing on this page reads them yet.
    minQuantity: Number(pricing.min_quantity) || 1,
    maxQuantity: Number(pricing.max_quantity) || 1,
    nextBriefSource: data?.next_brief_source ?? null,
    blockReason: data?.block_reason ?? null,
    // The backend's own state name. Kept for logging and for the order form's
    // conflict check; the page's rendering is driven by the numbers above, so
    // a new state name from the backend cannot blank the card.
    apiState: data?.state ?? null,
    timezone: data?.reset?.timezone ?? null,
  };
};

/**
 * The whole payload → what useCounterpartyBriefs() returns. `notFound` is the
 * 404 an unclaimed company answers with, which is the unclaimed gate and not
 * an error.
 */
export const mapAllowanceResponse = (data, { notFound = false } = {}) => {
  if (notFound || data == null) {
    return { isGate: true, gateVariant: GATE_VARIANTS.unclaimed, business: null, allowance: null };
  }

  if (data.claim_status !== "claimed") {
    return {
      isGate: true,
      gateVariant: gateVariantFor(data.claim_status),
      // Null on `unclaimed`, the business name on `pending`.
      business: data.business?.name ? data.business : null,
      allowance: null,
    };
  }

  return {
    isGate: false,
    gateVariant: null,
    business: data.business ?? null,
    allowance: mapAllowance(data),
  };
};

// ---------------------------------------------------------------------------
// TIP-610 — the briefs list and one brief's detail.
//
//   GET /api/briefs/orders/?company_id=<companyID>
//   GET /api/briefs/orders/<reference>/
//
// Three departures from the drafted contract are absorbed here, and each one
// is a thing the frontend now does that the document said it would not:
//
//   1. The envelope is flat. `results`, `current_page`, `total_pages` and
//      `total_results` sit at the top level; there is no `data` wrapper and no
//      `pagination` object.
//   2. The dates arrive as ISO timestamps, not as the `ordered_label` and
//      `completed_label` display strings the contract specified. So the
//      frontend formats them after all — see formatOrdered/formatCompleted,
//      which are the ONLY two date formatters in this feature.
//   3. There is no `status_counts`, no `download_url` and no `snag_reason` on
//      a row. The counts are therefore counted from the rows, which is why the
//      list query fetches every page rather than one.
// ---------------------------------------------------------------------------

// The list already sends the customer-facing string ("Request received"). The
// machine keys from the contract's one-vocabulary table are accepted too, so
// an environment that sends either renders the right chip.
export const STATUS_BY_API_KEY = {
  received: BRIEF_STATUSES.RECEIVED,
  building: BRIEF_STATUSES.BUILDING,
  ready: BRIEF_STATUSES.READY,
  returned: BRIEF_STATUSES.SNAG,
  // Admin's READY TO DELIVER is not visible to the customer: a brief with a
  // PDF attached but not yet delivered still reads as building.
  ready_to_deliver: BRIEF_STATUSES.BUILDING,
  delivered: BRIEF_STATUSES.READY,
  new: BRIEF_STATUSES.RECEIVED,
};

const DISPLAY_STATUSES = Object.values(BRIEF_STATUSES);

export const normaliseStatus = (status) => {
  if (typeof status !== "string") return null;
  const trimmed = status.trim();
  if (DISPLAY_STATUSES.includes(trimmed)) return trimmed;
  return STATUS_BY_API_KEY[trimmed.toLowerCase().replace(/[\s-]+/g, "_")] ?? null;
};

// Asia/Dubai, to agree with the timezone the allowance endpoint reports. A
// brief ordered at 01:30 Dubai time is "12 Aug" to the customer looking at it,
// whatever the browser's clock says.
const BRIEFS_TIMEZONE = "Asia/Dubai";

// Three letters, always. en-GB's own short month is "Sept" for September and
// three letters for the other eleven, which would put one odd row in a column
// of "12 Aug 2026". The design and the copy appendix use three throughout.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parts = (iso, options) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const found = new Intl.DateTimeFormat("en-GB", {
    timeZone: BRIEFS_TIMEZONE,
    ...options,
    // Numeric, so the month name comes from MONTHS and never from the locale.
    month: "numeric",
  })
    .formatToParts(date)
    .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});

  return {
    ...found,
    month: MONTHS[Number(found.month) - 1],
    // A numeric month makes Intl pad the day too. The design draws the day
    // unpadded ("9 Aug 2026") and the hour padded ("04:12 PM").
    day: String(Number(found.day)),
  };
};

/** "12 Aug 2026" — the Ordered column. */
export const formatOrdered = (iso) => {
  if (!iso) return null;
  const p = parts(iso, { day: "numeric", month: "short", year: "numeric" });
  return p ? `${p.day} ${p.month} ${p.year}` : null;
};

/**
 * "9 Aug 2026, 04:12 PM" — the Completed column. Day without a leading zero,
 * hour with one, meridiem upper case, exactly as the design draws it.
 */
export const formatCompleted = (iso) => {
  if (!iso) return null;
  const p = parts(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  if (!p) return null;
  const meridiem = (p.dayPeriod || "").replace(/\./g, "").toUpperCase();
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute}${meridiem ? ` ${meridiem}` : ""}`;
};

/**
 * One row of `results` → the row shape BriefsTable renders.
 *
 * `completed` is null while unfinished and the table draws an EMPTY CELL for
 * it — never a dash. `delivered_at` is the only source for it: a status of
 * Ready with no timestamp still leaves the cell empty rather than guessing.
 */
export const mapBriefRow = (row) => ({
  // The reference is the identity everywhere: the table's React key, the
  // detail request's path segment, and what the customer quotes to support.
  id: row?.reference ?? null,
  reference: row?.reference ?? null,
  company: row?.subject_registered_legal_name ?? "",
  country: row?.subject_country ?? "",
  status: normaliseStatus(row?.status),
  ordered: formatOrdered(row?.created_at),
  completed: formatCompleted(row?.delivered_at),
  // Kept raw for sorting and for the detail request; nothing renders these.
  orderedAt: row?.created_at ?? null,
  completedAt: row?.delivered_at ?? null,
  // Neither is on a list row today. The table hides the Download button
  // without a URL, and the snag modal falls back to its default reason.
  downloadUrl: row?.download_url ?? null,
  snagReason: row?.snag_reason ?? null,
});

/**
 * The flat list envelope → the rows and the totals.
 *
 * A row whose status is unrecognised is DROPPED rather than rendered without a
 * chip: a row with no status is a row the customer cannot act on, and it would
 * also miscount the filter.
 */
/**
 * `status_counts` → the counts the filter draws, keyed by display status.
 *
 * The keys the backend sends ARE the display statuses (BRIEF_STATUSES holds
 * the customer-facing labels verbatim), but they go through normaliseStatus
 * anyway so that an environment sending the machine keys still counts, and an
 * unrecognised key is dropped rather than drawn as a phantom option.
 *
 * Every published status is present, at zero if need be: the filter draws
 * "Snag 0" rather than omitting the option.
 */
export const mapStatusCounts = (counts, briefs = []) => {
  const zeroed = DISPLAY_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});

  if (counts && typeof counts === "object" && !Array.isArray(counts)) {
    return Object.entries(counts).reduce((acc, [key, value]) => {
      const status = normaliseStatus(key);
      const n = Number(value);
      if (!status || !Number.isFinite(n)) return acc;
      return { ...acc, [status]: (acc[status] || 0) + n };
    }, zeroed);
  }

  // No status_counts in the payload: count what is in hand. Correct only when
  // the rows in hand are the whole set.
  return briefs.reduce(
    (acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }),
    zeroed
  );
};

export const mapBriefsList = (payload) => {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const briefs = results.map(mapBriefRow).filter((b) => b.reference && b.status);

  return {
    briefs,
    // TIP-610 — the counts come from the BACKEND now (`status_counts`), keyed
    // by the same customer-facing labels BRIEF_STATUSES uses. They have to:
    // the table pages on the server, so counting the rows in hand would count
    // one page of ten and report "Ready 2" to a customer with thirty.
    //
    // The server computes them over the whole unfiltered set, so choosing a
    // status does not rewrite the numbers beside the other statuses.
    //
    // Falling back to counting the rows keeps an older backend rendering
    // something sane rather than a filter of zeroes.
    statusCounts: mapStatusCounts(payload?.status_counts, briefs),
    // `total_results` is the FILTERED count — what "Showing 1 to 10 of N"
    // must say. `total_all` is every brief this company has ordered, which is
    // the number beside "All statuses" and the one that decides whether the
    // empty state belongs on screen.
    total: Number(payload?.total_results ?? briefs.length) || briefs.length,
    totalAll: Number(payload?.total_all ?? payload?.total_results ?? briefs.length) || 0,
    totalPages: Number(payload?.total_pages ?? 1) || 1,
    currentPage: Number(payload?.current_page ?? 1) || 1,
    // How many rows were dropped for want of a usable status or reference.
    // Nothing renders this yet; it exists so that a vocabulary change on the
    // backend is one assertion away from being caught, rather than showing up
    // as rows quietly going missing.
    skipped: results.length - briefs.length,
  };
};

// `plan` is what the endpoints actually send; the contract drafted it as
// `allowance`. Both mean the monthly allowance.
export const SOURCE_BUCKETS = { plan: "plan", allowance: "plan", bought: "bought" };

export const normaliseSourceBucket = (bucket) =>
  typeof bucket === "string" ? SOURCE_BUCKETS[bucket.trim().toLowerCase()] ?? null : null;

/**
 * The detail payload → what the status modal shows.
 *
 * `analyst_notes` is the note the CUSTOMER wrote to the analyst in the order
 * form. It is not a snag reason, and it must not be shown as one — the list
 * and the detail both still lack the reason a returned brief was returned.
 */
export const mapBriefDetail = (data) => {
  if (data == null) return null;
  return {
    reference: data.reference ?? null,
    status: normaliseStatus(data.status),
    sourceBucket: normaliseSourceBucket(data.source_bucket),
    ordered: formatOrdered(data.created_at),
    orderedAt: data.created_at ?? null,
    requesterName: data.requester_full_name ?? null,
    requesterEmail: data.requester_business_email ?? null,
    company: data.subject_registered_legal_name ?? null,
    country: data.subject_country ?? null,
    purpose: data.purpose_of_request ?? null,
    // The customer's own words, echoed back so they can see what they asked
    // for. Never presented as the analyst's answer.
    notes: data.analyst_notes ?? null,
  };
};
